/**
 * Fix confidence — how much we should trust a plotted fix.
 *
 * Two things drive the quality of a fix, and a navigator judges both by eye:
 *   1. The angle of cut. Position lines that cross near a right angle give a
 *      crisp fix; lines that meet at a fine angle smear the position along their
 *      length, so a small error in either line moves the fix a long way.
 *   2. The cocked hat. Three or more lines rarely meet at a point, and the size
 *      of the triangle they make is a direct measure of the error.
 *
 * We fold both into a single 0..1 value and band it Low / Medium / High. The
 * banded shape matches MarkingResult.confidence in the technical spec, which is
 * a fixed schema requirement, so the assessment layer reads it straight through.
 *
 * This is a teaching-grade confidence, not a formal error ellipse. It is honest
 * about the two things that matter and is unit-tested at its boundaries.
 */
import type { PositionLine, FixResult } from './fix';
import { normaliseBearing, rhumbBearingDeg } from './geodesy';

export interface BandedConfidence {
  /** 0..1. */
  value: number;
  label: 'Low' | 'Medium' | 'High';
}

/** A cocked hat this big (nm) or larger scores zero for size. */
const SIZE_REF_NM = 0.5;
const HIGH_AT = 0.7;
const MEDIUM_AT = 0.4;

export function bandConfidence(value: number): BandedConfidence {
  const v = clamp01(value);
  const label = v >= HIGH_AT ? 'High' : v >= MEDIUM_AT ? 'Medium' : 'Low';
  return { value: v, label };
}

/**
 * The direction of a position line at a given point, in degrees true.
 * A bearing line runs along its bearing. A range circle's tangent at the point
 * is perpendicular to the radius from the centre to that point.
 */
export function lineDirectionAt(line: PositionLine, at: [number, number]): number {
  if (line.type === 'bearing') {
    return normaliseBearing(line.bearingDeg);
  }
  const radial = rhumbBearingDeg(line.centre, at);
  return normaliseBearing(radial + 90);
}

/**
 * The acute angle of cut between two directions, in (0, 90]. Direction sign and
 * reciprocals do not matter for a line, so we fold the difference into 0..90.
 */
export function angleOfCutDeg(dirADeg: number, dirBDeg: number): number {
  let diff = Math.abs(normaliseBearing(dirADeg) - normaliseBearing(dirBDeg)) % 180;
  if (diff > 90) diff = 180 - diff;
  return diff;
}

/**
 * Confidence for a fix from its position lines and the fix result.
 *
 *   cutScore  = sin(worst pairwise angle of cut). 90 deg -> 1, 30 deg -> 0.5,
 *               near-parallel -> near 0.
 *   sizeScore = 1 - (cocked-hat size / SIZE_REF), clamped to 0..1. A two-line
 *               fix has a zero-size cocked hat, so size does not penalise it.
 *   value     = cutScore * sizeScore (both must be good for a good fix).
 */
export function fixConfidence(lines: PositionLine[], fix: FixResult): BandedConfidence {
  if (lines.length < 2) return bandConfidence(0);

  const dirs = lines.map((l) => lineDirectionAt(l, fix.position));
  let worstCut = 90;
  for (let i = 0; i < dirs.length; i++) {
    for (let j = i + 1; j < dirs.length; j++) {
      const cut = angleOfCutDeg(dirs[i], dirs[j]);
      if (cut < worstCut) worstCut = cut;
    }
  }
  const cutScore = Math.sin((worstCut * Math.PI) / 180);
  const sizeScore = clamp01(1 - fix.uncertaintyNm / SIZE_REF_NM);
  return bandConfidence(cutScore * sizeScore);
}

function clamp01(x: number): number {
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}
